import { EmailAddress, PhoneNumber } from '@/database/baseTypes';
import { Divider, Group, Stack, TextInput, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { ContactSearchSelection } from '../../../ContactSearchField/ContactSearchField';
import { HumanContact, OrganizationContact } from '../../types';
import AddContactPanel from './AddContactPanel';
import { EmailAddressFields } from './EmailAddressFields';
import HumanContactForm from './HumanContactForm';
import classes from './OrganizationOutline.module.css';
import { PhoneNumberFields } from './PhoneNumberFields';
import { Text } from '@mantine/core';
import { AtSignIcon, Building2Icon, PhoneIcon, UserIcon } from 'lucide-react';
import { CollapsibleContactCard } from './CollapsibleContactCard';
import PrimaryContactButton from './PrimaryContactButton';

interface Props {
  organization: OrganizationContact;
  primaryContactTempId: string | null;
  autoFocus: 'companyName' | 'contactSearch' | 'none';
  onContactSearchSelection: (selection: ContactSearchSelection) => void;
  getAlreadyAddedContactIds: () => string[];
  humanCanBePrimary: boolean;
  onHumanWantsPrimary: (tempId: string) => void;
  onHumanChange: (human: HumanContact) => void;
  onHumanDelete: (index: number) => void;
  onOrganizationDelete: () => void;
  onOrganizationChange: (organization: OrganizationContact) => void;
}

export const OrganizationOutline = ({
  organization,
  onContactSearchSelection,
  getAlreadyAddedContactIds,
  primaryContactTempId,
  humanCanBePrimary,
  onHumanWantsPrimary,
  onHumanDelete,
  onHumanChange,
  onOrganizationDelete,
  onOrganizationChange,
}: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(
    organization.firestoreId !== null,
  );
  const [isAddContactFormCollapsed, setIsAddContactFormCollapsed] =
    useState(true);

  const handleContactSearchSelection = (selection: ContactSearchSelection) => {
    onContactSearchSelection(selection);
    setIsAddContactFormCollapsed(true);
  };

  return (
    <CollapsibleContactCard
      icon={<Building2Icon size={16} strokeWidth={2} />}
      title={<OrganizationTitle organization={organization} />}
      accessibilityLabel={labelForOrganization(organization)}
      canDelete={true}
      onDelete={onOrganizationDelete}
      canMakePrimary={false}
      isPrimary={false}
      onWantsPrimary={() => {}}
      childrenWhenCollapsed={
        <CollapsedOrganizationInfo
          organization={organization}
          primaryContactTempId={primaryContactTempId}
          onRepresentativeWantsPrimary={onHumanWantsPrimary}
        />
      }
      childrenWhenExpanded={
        <ExpandedOrganizationInfo
          organization={organization}
          primaryContactTempId={primaryContactTempId}
          humanCanBePrimary={humanCanBePrimary}
          onHumanWantsPrimary={onHumanWantsPrimary}
          onHumanChange={onHumanChange}
          onHumanDelete={onHumanDelete}
          onContactSearchSelection={handleContactSearchSelection}
          getAlreadyAddedContactIds={getAlreadyAddedContactIds}
          onOrganizationChange={onOrganizationChange}
          onToggleAddContactFormCollapse={() => {
            setIsAddContactFormCollapsed(!isAddContactFormCollapsed);
          }}
          isAddContactFormCollapsed={isAddContactFormCollapsed}
        />
      }
      isCollapsed={isCollapsed}
      onToggleCollapse={() => {
        setIsCollapsed(!isCollapsed);
      }}
    />
  );
};

const labelForOrganization = (organization: OrganizationContact) => {
  return organization.companyName !== ''
    ? organization.companyName
    : 'Unnamed company';
};

const OrganizationTitle = ({
  organization,
}: {
  organization: OrganizationContact;
}) => {
  return (
    <Title
      order={3}
      size='h4'
      style={{ margin: '0', color: 'var(--mantine-color-text)' }}
    >
      {labelForOrganization(organization)}
    </Title>
  );
};

const ExpandedOrganizationInfo = ({
  organization,
  primaryContactTempId,
  humanCanBePrimary,
  onHumanWantsPrimary,
  onHumanChange,
  onHumanDelete,
  onContactSearchSelection,
  getAlreadyAddedContactIds,
  onOrganizationChange,
  onToggleAddContactFormCollapse,
  isAddContactFormCollapsed,
}: {
  organization: OrganizationContact;
  primaryContactTempId: string | null;
  humanCanBePrimary: boolean;
  onHumanWantsPrimary: (tempId: string) => void;
  onHumanChange: (human: HumanContact) => void;
  onHumanDelete: (index: number) => void;
  onContactSearchSelection: (selection: ContactSearchSelection) => void;
  getAlreadyAddedContactIds: () => string[];
  onOrganizationChange: (organization: OrganizationContact) => void;
  onToggleAddContactFormCollapse: () => void;
  isAddContactFormCollapsed: boolean;
}) => {
  return (
    <Stack px='lg' gap='lg'>
      <ExpandedOrganizationContactInfoForm
        organization={organization}
        canEditContactInfo={true}
        onChange={onOrganizationChange}
      />

      <div className={classes.representativesContainer}>
        <Title order={4} fw={500} mb='md'>
          Company Representatives
        </Title>
        <ul
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          {organization.representatives.map((rep, index) => (
            <li key={`${rep.tempId}`} className={classes.representativeRow}>
              <HumanContactForm
                title={`Representative`}
                canBePrimary={humanCanBePrimary}
                titleSize='md'
                human={rep}
                isPrimary={primaryContactTempId === rep.tempId}
                onChange={onHumanChange}
                onWantsPrimary={() => onHumanWantsPrimary(rep.tempId)}
                onDelete={() => onHumanDelete(index)}
              />
            </li>
          ))}
          <AddContactPanel
            panelTitle='Add a representative'
            searchFieldLabel='Search for an existing contact'
            collapsedAccessibilityLabel='Expand options to add a representative'
            expandedAccessibilityLabel='Hide options to add a representative'
            onContactSelection={(selection) => {
              onContactSearchSelection(selection);
            }}
            getAlreadyAddedContactIds={getAlreadyAddedContactIds}
            searchFieldPlaceholder='Search for an existing contact'
            searchMode='human'
            isCollapsed={isAddContactFormCollapsed}
            onToggleCollapse={onToggleAddContactFormCollapse}
          />
        </ul>
      </div>
    </Stack>
  );
};

const ExpandedOrganizationContactInfoForm = ({
  organization,
  canEditContactInfo,
  onChange,
}: {
  organization: OrganizationContact;
  canEditContactInfo: boolean;
  onChange: (organization: OrganizationContact) => void;
}) => {
  const [companyName, setCompanyName] = useState(organization.companyName);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>(
    organization.phoneNumbers ?? [],
  );
  const [emailAddresses, setEmailAddresses] = useState<EmailAddress[]>(
    organization.emailAddresses ?? [],
  );
  const [primaryPhoneNumberId, setPrimaryPhoneNumberId] = useState<string>(
    organization.primaryPhoneNumberId ?? '',
  );
  const [primaryEmailAddressId, setPrimaryEmailAddressId] = useState<string>(
    organization.primaryEmailAddressId ?? '',
  );

  useEffect(() => {
    onChange({
      tempId: organization.tempId,
      firestoreId: organization.firestoreId,
      role: organization.role,
      companyName,
      phoneNumbers,
      emailAddresses,
      primaryPhoneNumberId,
      primaryEmailAddressId,
      type: 'organization',
      representatives: organization.representatives,
    });
  }, [
    companyName,
    phoneNumbers,
    emailAddresses,
    primaryPhoneNumberId,
    primaryEmailAddressId,
  ]);

  return (
    <Stack align='stretch' flex={1}>
      <Stack gap='lg'>
        <Group>
          <TextInput
            label='Company name'
            key={`${organization.tempId}-companyName`}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            autoComplete='off'
            flex={1}
          />
        </Group>
        {canEditContactInfo && (
          <>
            <PhoneNumberFields
              data={{
                items: phoneNumbers,
                primaryItemId: primaryPhoneNumberId,
              }}
              onChange={(data) => {
                setPhoneNumbers(data.items);
                setPrimaryPhoneNumberId(data.primaryItemId ?? '');
              }}
            />
            <EmailAddressFields
              data={{
                items: emailAddresses,
                primaryItemId: primaryEmailAddressId,
              }}
              onChange={(data) => {
                setEmailAddresses(data.items);
                setPrimaryEmailAddressId(data.primaryItemId ?? '');
              }}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};

const CollapsedOrganizationInfo = ({
  organization,
  primaryContactTempId,
  onRepresentativeWantsPrimary,
}: {
  organization: OrganizationContact;
  primaryContactTempId: string | null;
  onRepresentativeWantsPrimary: (tempId: string) => void;
}) => {
  const hasContactInfo =
    organization.phoneNumbers.length > 0 ||
    organization.emailAddresses.length > 0;

  const hasRepresentatives = organization.representatives.length > 0;

  if (!hasContactInfo && !hasRepresentatives) {
    return null;
  }

  return (
    <Stack gap='xs' p='0'>
      {hasContactInfo && (
        <CollapsedOrganizationContactMethods organization={organization} />
      )}
      {hasRepresentatives && (
        <Divider label='Representatives' labelPosition='left' py='xs' />
      )}
      {hasRepresentatives && (
        <CollapsedOrganizationRepresentatives
          organization={organization}
          primaryContactTempId={primaryContactTempId}
          onRepresentativeWantsPrimary={onRepresentativeWantsPrimary}
        />
      )}
    </Stack>
  );
};

const CollapsedOrganizationContactMethods = ({
  organization,
}: {
  organization: OrganizationContact;
}) => {
  return (
    <Stack gap='0.25rem'>
      {organization.phoneNumbers.length > 0 && (
        <Group gap='xs'>
          <PhoneIcon size={14} color='var(--mantine-color-dimmed)' />
          <Text size='sm' c='dimmed'>
            {organization.phoneNumbers.map((phone) => phone.value).join(', ')}
          </Text>
        </Group>
      )}
      {organization.emailAddresses.length > 0 && (
        <Group gap='xs'>
          <AtSignIcon size={14} color='var(--mantine-color-dimmed)' />
          <Text size='sm' c='dimmed'>
            {organization.emailAddresses.map((email) => email.value).join(', ')}
          </Text>
        </Group>
      )}
    </Stack>
  );
};

const CollapsedOrganizationRepresentatives = ({
  organization,
  primaryContactTempId,
  onRepresentativeWantsPrimary,
}: {
  organization: OrganizationContact;
  primaryContactTempId: string | null;
  onRepresentativeWantsPrimary: (tempId: string) => void;
}) => {
  const hasRepresentatives = organization.representatives.length > 0;

  if (!hasRepresentatives) {
    return null;
  }

  return (
    <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {organization.representatives.map((rep) => (
        <li key={rep.tempId}>
          <Stack gap='0'>
            <Group gap='xs'>
              <UserIcon size={16} />
              <Text size='sm'>
                {rep.firstName !== '' || rep.lastName !== ''
                  ? `${rep.firstName} ${rep.lastName}`
                  : 'Unnamed representative'}
              </Text>
              <PrimaryContactButton
                isPrimary={primaryContactTempId === rep.tempId}
                onWantsPrimary={() => onRepresentativeWantsPrimary(rep.tempId)}
              />
            </Group>

            <Stack gap='0.25rem' pb='0.25rem'>
              {rep.phoneNumbers.length > 0 && (
                <Group gap='xs'>
                  <PhoneIcon size={14} color='var(--mantine-color-gray-8)' />
                  <Text size='sm' c='gray.8'>
                    {rep.phoneNumbers.map((phone) => phone.value).join(', ')}
                  </Text>
                </Group>
              )}
              {rep.emailAddresses.length > 0 && (
                <Group gap='xs'>
                  <AtSignIcon size={14} color='var(--mantine-color-gray-8)' />
                  <Text size='sm' c='gray.8'>
                    {rep.emailAddresses.map((email) => email.value).join(', ')}
                  </Text>
                </Group>
              )}
            </Stack>
          </Stack>
        </li>
      ))}
    </ul>
  );
};
